import { Component, OnDestroy, OnInit, computed, input, output } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { SearchEntityType, SearchManyResponse } from '@ftr/contracts/api/search'
import { Configuration } from '@ftr/contracts/type/court'
import { RemoteData } from '@ftr/foundation'
import { SearchFocusState } from '@ftr/ui-search'

import { SearchResultViewModel } from '~app/features/search/search-results/search-results-list/search-results-list.component'
import { SearchTypeFilterItemMetadata } from '~app/features/search/search-type-filter/search-type-filter.component'
import { SearchOverlayService } from '~app/services/search-overlay/search-overlay.service'

type SearchTypeFilterItemBase = Omit<SearchTypeFilterItemMetadata, 'selected' | 'numberOfResults'>

@Component({
  selector: 'ftr-search-results-overlay',
  templateUrl: './search-results-overlay.component.html',
  styleUrls: ['./search-results-overlay.component.css'],
})
export class SearchResultsOverlay implements OnInit, OnDestroy {
  isSearching = input.required<boolean>()
  entitiesToSearch = input.required<SearchEntityType[]>()
  searchTerm = input.required<string>()
  searchResponse = input.required<RemoteData<SearchManyResponse>>()
  searchFocusState = input.required<SearchFocusState>()
  configuration = input.required<Configuration>()
  availableEntities = input.required<SearchEntityType[]>()
  searchEntityTypeToNumberOfMatches = input.required<ReadonlyMap<SearchEntityType, number>>()

  onFilterItemSelected = output<SearchEntityType>()
  onItemSelected = output<SearchResultViewModel>()
  onNewSearchFocusState = output<SearchFocusState>()
  onRetry = output()

  protected isSearching$ = toObservable(this.isSearching)
  private filterItemsForSelection = computed<SearchTypeFilterItemBase[]>(() => {
    const options = this.availableEntities()
    return (
      [
        {
          icon: 'PlayCircleOutline',
          label: 'Recordings',
          type: SearchEntityType.Recordings,
        },
        {
          icon: 'AccountCircle',
          label: 'Orders',
          type: SearchEntityType.Orders,
        },
      ] satisfies SearchTypeFilterItemBase[]
    ).filter(x => options.some(y => y === x.type))
  })

  availableFilterItems = computed<SearchTypeFilterItemMetadata[]>(() => {
    const searchTypes = this.entitiesToSearch()
    const searchEntityTypeToNumberOfMatches = this.searchEntityTypeToNumberOfMatches()
    return this.filterItemsForSelection()
      .map(x => ({
        ...x,
        numberOfResults: searchEntityTypeToNumberOfMatches.get(x.type),
        selected: searchTypes.includes(x.type),
      }))
      .filter(x => x.numberOfResults !== 0)
  })

  constructor(readonly overlay: SearchOverlayService) {}

  ngOnInit(): void {
    this.overlay.visible.next(true)
  }

  ngOnDestroy(): void {
    // Set timeout to avoid ExpressionChanged. We want to wait for
    // change detection to finish before updating anything
    setTimeout(() => {
      this.overlay.visible.next(false)
    })
  }

  handleFilterItemSelected(type: SearchEntityType): void {
    this.onFilterItemSelected.emit(type)
    this.onNewSearchFocusState.emit(SearchFocusState.RESULTS_LIST)
  }
}
